import { Injectable } from "@angular/core";
import { Route, UrlTree } from "@angular/router";
import { Store } from "@ngrx/store";
import { Console } from "console";
import { Observable, zip } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";
import { ClientSettings } from "src/model/class/client-settings";
import { Plant } from "src/model/class/general classes/plant";
import { Supervision } from "src/model/class/general classes/supervision";
import { Settings } from "src/model/class/settings";
import { BIGDeviceOperationMode } from "src/components/shared-components/enum/enumDevice";
import { EnvironmentCategory } from "src/components/shared-components/enum/enumEnvironment";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { PageSection } from "src/model/enum/enumPage";
import { LoggerService } from "src/service/logger.service";
import { SupervisionService } from "src/service/supervision.service";
import { SystemRouterService } from "src/service/system-router.service";
import { clientSettingsUpdateCustomStartingAccessEnvironmentcartegoryAction } from "src/state/actions/client-settings.actions";
import { clientSettings } from "src/state/selectors/client-settings.selectors";
import { selectedPlant } from "src/state/selectors/plant.selectors";
import { serverSettings } from "src/state/selectors/server-settings-selector";

/**
 * Handle first page redirect after login
 */
@Injectable({
  providedIn: "root",
})
export class IntermediateRedirectGuard  {
  private _logContext: string = "Intermediate-redirect.guard";
  private _clientSetting: ClientSettings = null;
  private _currentPlant: Plant = null;
  private _userSettings: Settings = null;

  constructor(
    private _systemRouterService: SystemRouterService,
    private _supervisionService: SupervisionService,
    private _loggerService: LoggerService,
    private _store: Store
  ) { }

  canLoad(
    route: Route
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "canLoad()",
      "START"
    );

    return this.zipClientSettingsCurrentPlant();
  }

  /**
   * Zip multiple client observable into once
   */
  private zipClientSettingsCurrentPlant(): Observable<boolean> {
    return zip(
      this._store.select(clientSettings).pipe(
        filter((settings) => settings !== null),

        take(1),
        tap((clientSettings) => {
          this._clientSetting = clientSettings;

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "zipClientSettingsCurrentPlant()",
            "SELECTED clientSettings FROM STORE"
          );
        })
      ),
      this._store.select(selectedPlant).pipe(
        filter((currentPlant) => currentPlant !== null),
        take(1),
        tap((currentPlant) => {
          this._currentPlant = currentPlant;
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "zipClientSettingsCurrentPlant()",
            "SELECTED selectedPlant FROM STORE"
          );
        })
      ),
      this._store.select(serverSettings).pipe(
        filter((serverSettings) => serverSettings !== null),
        take(1),
        tap((serverSettings) => {
          this._userSettings = serverSettings;

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "zipClientSettingsCurrentPlant()",
            "SELECTED serverSettings FROM STORE"
          );
        })
      )
    ).pipe(
      tap((objectResult: Array<any>) =>
        this.handleCurrentPlantObjectResult(objectResult)
      ),
      map((objectResult: any) => {
        return false;
      })
    );
  }

  /**
   * Calculate first page to open //
   *
   * @param objectResult Object obtained from zip procedure
   */
  private handleCurrentPlantObjectResult(objectResult: Array<any>): void {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleCurrentPlantObjectResult()",
      "START"
    );

    if (this._currentPlant.defaultSupervision === "-1,0") {
      this.navigateToFirstUserPageByUserSettings();
    } else {
      this._supervisionService.handleClientSettingsDefaultPageToOpen();
    }
  }

  /**
   * Navigate to first page expressed in DefaultHomepageUser parameter.
   * If this parameter not exist open defualt
   *

   */
  private navigateToFirstUserPageByUserSettings(): void {
    const firstPageToOpen: number = this.defineFirstPageToOpen();
    /*
      Number(this._userSettings.DefaultHomepageUser) ||
      PageSection.SUPERVISION_CUSTOM_INTERFACE_LIST;
      */

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "navigateToFirstUserPageByUserSettings",
      "firstPageToOpen: " + firstPageToOpen
    );

    this._supervisionService.Init();

    this.setDefaultAccessTabToOpenByFirstPage();

    if (
      firstPageToOpen === PageSection.SUPERVISION_CUSTOM_INTERFACE ||
      firstPageToOpen === PageSection.SUPERVISION_CUSTOM_INTERFACE_LIST
    ) {
      this.openSupervisionBySupervisionCount();
    } else {
      this._systemRouterService.openPageByPageSection(firstPageToOpen);
    }
  }

  /**
   * This method defines the actual landing page, applying all the needed conditions and considerations to the putative page the
   * user should be eligible to by server-side configuration.
   * @returns
   */
  private defineFirstPageToOpen(): number {
    let firstPageActual: number =
      Number(this._userSettings.DefaultHomepageUser) ||
      PageSection.SUPERVISION_CUSTOM_INTERFACE_LIST;

    if (
      this._currentPlant?.operativeMode ===
      BIGDeviceOperationMode.ACCESS_CONTROL_MODE
    ) {
      if (
        firstPageActual !== PageSection.SUPERVISION_CUSTOM_INTERFACE &&
        firstPageActual !== PageSection.SUPERVISION_CUSTOM_INTERFACE_LIST
      ) {
        firstPageActual = PageSection.SUPERVISION_CUSTOM_INTERFACE_LIST;
      }
    }

    return firstPageActual;
  }

  /**
   * navigate to specific supervision
   *
   * @param idSupervision ID supervision
   * @param idEnvironment ID environment
   */
  private navigateToSpecificSupervision(
    idSupervision: number,
    idEnvironment: number
  ): void {
    if (this._clientSetting.supervisionListViewMode === true) {
      this.navigateToSpecificSupervisionList(idSupervision, idEnvironment);
    } else {
      this.navigateToSpecificSupervisionGraph(idSupervision, idEnvironment);
    }
  }

  /**
   * Navigate into specific supervision list mode based on idSupervision and idEnvironment
   *
   * @param idSupervision ID Supervision to open
   * @param idEnvironment ID Environment to open
   */
  private navigateToSpecificSupervisionList(
    idSupervision: number,
    idEnvironment: number
  ): void {
    this._systemRouterService.navigateToSupervisionList(
      idSupervision,
      idEnvironment
    );
  }

  /**
   * Navigate into specific supervision graph mode based on idSupervision and idEnvironment
   *
   * @param idSupervision ID Supervision to open
   * @param idEnvironment ID Environment to open
   */
  private navigateToSpecificSupervisionGraph(
    idSupervision: number,
    idEnvironment: number
  ): void {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "navigateToSpecificSupervisionGraph",
      "Page to open: idSupervision: " +
      idSupervision +
      " , idEnvironment: " +
      idEnvironment
    );

    this._systemRouterService.navigateToSupervisionGraph(
      idSupervision,
      idEnvironment
    );
  }

  /**
   * Set tab to open
   */
  private async setDefaultAccessTabToOpenByFirstPage(): Promise<void> {
    let landingAccessTab: number = EnvironmentCategory.COMMON_AREA;

    // Da gestire solo page section legate agli accessi //
    switch (Number(this._userSettings.DefaultHomepageUser)) {
      case PageSection.ACCESSES_COMMON:
        landingAccessTab = EnvironmentCategory.COMMON_AREA;
        break;
      case PageSection.ACCESSES_ROOM:
        landingAccessTab = EnvironmentCategory.ROOM;
        break;
      case PageSection.ACCESSES_SUITE:
        landingAccessTab = EnvironmentCategory.SUITE;
        break;
      case PageSection.ACCESSES_JUNIOR_SUITE:
        landingAccessTab = EnvironmentCategory.JUNIOR_SUITE;
        break;
      case PageSection.ACCESSES_SINGLE_ROOM:
        landingAccessTab = EnvironmentCategory.SINGLE_ROOM;
        break;
      case PageSection.ACCESSES_DOUBLE_ROOM:
        landingAccessTab = EnvironmentCategory.DOUBLE_ROOM;
        break;
      case PageSection.ACCESSES_TRIPLE_ROOM:
        landingAccessTab = EnvironmentCategory.TRIPLE_ROOM;
        break;
      case PageSection.ACCESSES_FAMILY_ROOM:
        landingAccessTab = EnvironmentCategory.FAMILY_ROOM;
        break;
      case PageSection.ACCESSES_DORMITORY:
        landingAccessTab = EnvironmentCategory.DORMITORY;
        break;
      case PageSection.ACCESSES_ELEVATOR_FLOOR:
        landingAccessTab = EnvironmentCategory.ELAVATOR_FLOOR;
        break;
      case PageSection.ACCESSES_CABINETS:
        landingAccessTab = EnvironmentCategory.CABINETS;
        break;
      case PageSection.ACCESSES_SKI_DEPOSITIS:
        landingAccessTab = EnvironmentCategory.SKI_DEPOSITS;
        break;
      case PageSection.ACCESSES_EXTERNAL_ACCESS:
        landingAccessTab = EnvironmentCategory.EXTERNAL_ACCESS;
        break;
      default:
        landingAccessTab = EnvironmentCategory.COMMON_AREA;
        break;
    }

    this._store.dispatch(
      clientSettingsUpdateCustomStartingAccessEnvironmentcartegoryAction({
        accessCategory: landingAccessTab,
      })
    );
  }

  /**
   * Check number supervision are avaiable
   */
  private openSupervisionBySupervisionCount(): void {
    this._supervisionService.listSupervision
      .pipe(
        filter(
          (listSupervision: Array<Supervision>) => listSupervision !== null
        ),
        take(1),
        map((listSupervision: Array<any>) => {
          const supervisionTypedList: Supervision[] = listSupervision.map(
            (supervision) => new Supervision(supervision)
          );

          return supervisionTypedList;
        })
      )
      .subscribe((listSupervision: Array<Supervision>) => {
        this.handleSupervisionNavigationBySupervisionCount(listSupervision);
      });
  }

  /**
   * handle specific page supervision open or list supervision open
   *
   * @param listSupervision List supervision
   */
  private handleSupervisionNavigationBySupervisionCount(
    listSupervision: Array<Supervision>
  ): void {
    if (listSupervision.length !== 1) {
      this._systemRouterService.navigateToListSupervisions();
    } else {
      const idSupervision: number = Number(listSupervision[0].id);
      const idEnvironment: number = Number(listSupervision[0].idEnvironment);

      this.navigateToSpecificSupervision(idSupervision, idEnvironment);
    }
  }
}
